import {
  forwardRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react'
import { cn } from '@lib/utils'

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Input label */
  label?: string
  /** Helper text below input */
  helperText?: string
  /** Error message (also sets error state) */
  error?: string
  /** Input size */
  size?: 'sm' | 'md' | 'lg'
  /** Icon on left side */
  leftIcon?: ReactNode
  /** Icon/element on right side */
  rightIcon?: ReactNode
  /** Full width input */
  fullWidth?: boolean
}

/**
 * Text input component with brutalist styling
 *
 * @example
 * <Input label="Email" placeholder="Enter email" />
 * <Input label="Password" type="password" />
 * <Input error="This field is required" />
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      helperText,
      error,
      size = 'md',
      leftIcon,
      rightIcon,
      fullWidth = false,
      disabled,
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || `input-${Math.random().toString(36).slice(2, 9)}`
    const hasError = !!error

    return (
      <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={inputId}
            className="text-body-sm font-medium text-text-primary"
          >
            {label}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">
              {leftIcon}
            </span>
          )}

          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            className={cn(
              // Base styles
              'w-full bg-surface text-text-primary placeholder:text-text-muted',
              'border transition-all',
              'focus:outline-none',

              // Size variants
              size === 'sm' && 'h-8 px-3 text-body-sm',
              size === 'md' && 'h-10 px-4 text-body',
              size === 'lg' && 'h-12 px-4 text-body-lg',

              // Icon padding adjustments
              leftIcon && size === 'sm' && 'pl-9',
              leftIcon && size === 'md' && 'pl-10',
              leftIcon && size === 'lg' && 'pl-12',
              rightIcon && size === 'sm' && 'pr-9',
              rightIcon && size === 'md' && 'pr-10',
              rightIcon && size === 'lg' && 'pr-12',

              // States
              !hasError && [
                'border-border',
                'hover:border-border-strong',
                'focus:border-primary focus:shadow-brutalist',
              ],
              hasError && [
                'border-error',
                'focus:border-error focus:shadow-brutalist-error',
              ],
              disabled && [
                'bg-surface-elevated border-border-subtle',
                'text-text-muted cursor-not-allowed',
              ],

              className
            )}
            aria-invalid={hasError}
            aria-describedby={
              hasError ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
            }
            {...props}
          />

          {rightIcon && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted">
              {rightIcon}
            </span>
          )}
        </div>

        {(error || helperText) && (
          <p
            id={error ? `${inputId}-error` : `${inputId}-helper`}
            className={cn(
              'text-caption',
              error ? 'text-error' : 'text-text-muted'
            )}
          >
            {error || helperText}
          </p>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

/**
 * Password input with show/hide toggle
 */
export const PasswordInput = forwardRef<
  HTMLInputElement,
  Omit<InputProps, 'type' | 'rightIcon'>
>((props, ref) => {
  const [showPassword, setShowPassword] = useState(false)

  return (
    <Input
      ref={ref}
      type={showPassword ? 'text' : 'password'}
      rightIcon={
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="text-text-muted hover:text-text-primary transition-colors"
          tabIndex={-1}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      }
      {...props}
    />
  )
})

PasswordInput.displayName = 'PasswordInput'

/**
 * Search input with search icon and clear button
 */
export interface SearchInputProps extends Omit<InputProps, 'leftIcon' | 'rightIcon'> {
  /** Called when search value changes */
  onSearch?: (value: string) => void
  /** Show clear button when input has value */
  clearable?: boolean
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ onSearch, clearable = true, value, onChange, ...props }, ref) => {
    const [internalValue, setInternalValue] = useState('')
    const currentValue = value !== undefined ? value : internalValue

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setInternalValue(newValue)
      onChange?.(e)
      onSearch?.(newValue)
    }

    const handleClear = () => {
      setInternalValue('')
      onSearch?.('')
    }

    return (
      <Input
        ref={ref}
        type="search"
        value={currentValue}
        onChange={handleChange}
        leftIcon={<SearchIcon />}
        rightIcon={
          clearable && currentValue ? (
            <button
              type="button"
              onClick={handleClear}
              className="text-text-muted hover:text-text-primary transition-colors"
              tabIndex={-1}
              aria-label="Clear search"
            >
              <CloseIcon />
            </button>
          ) : null
        }
        {...props}
      />
    )
  }
)

SearchInput.displayName = 'SearchInput'

/**
 * Textarea component
 */
export interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  label?: string
  helperText?: string
  error?: string
  fullWidth?: boolean
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    { className, label, helperText, error, fullWidth = false, disabled, id, ...props },
    ref
  ) => {
    const textareaId = id || `textarea-${Math.random().toString(36).slice(2, 9)}`
    const hasError = !!error

    return (
      <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={textareaId}
            className="text-body-sm font-medium text-text-primary"
          >
            {label}
          </label>
        )}

        <textarea
          ref={ref}
          id={textareaId}
          disabled={disabled}
          className={cn(
            // Base styles
            'w-full min-h-[100px] px-4 py-3 bg-surface text-text-primary',
            'placeholder:text-text-muted border transition-all resize-y',
            'focus:outline-none',

            // States
            !hasError && [
              'border-border',
              'hover:border-border-strong',
              'focus:border-primary focus:shadow-brutalist',
            ],
            hasError && [
              'border-error',
              'focus:border-error focus:shadow-brutalist-error',
            ],
            disabled && [
              'bg-surface-elevated border-border-subtle',
              'text-text-muted cursor-not-allowed resize-none',
            ],

            className
          )}
          aria-invalid={hasError}
          aria-describedby={
            hasError ? `${textareaId}-error` : helperText ? `${textareaId}-helper` : undefined
          }
          {...props}
        />

        {(error || helperText) && (
          <p
            id={error ? `${textareaId}-error` : `${textareaId}-helper`}
            className={cn(
              'text-caption',
              error ? 'text-error' : 'text-text-muted'
            )}
          >
            {error || helperText}
          </p>
        )}
      </div>
    )
  }
)

Textarea.displayName = 'Textarea'

// Icon components
function EyeIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

function EyeOffIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  )
}

function SearchIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

export default Input
